import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

// Types
export interface TrackedObject {
  id: string;
  name: string;
  description?: string;
  type?: string;
  createdAt: number;
  updatedAt: number;
  iconUrl?: string; // Custom icon URL for the object
  idToken?: string; // Sidewalk API ID token
  refreshToken?: string; // Sidewalk API refresh token
  encryptedUsername?: string; // KMS-encrypted Sidewalk username
  encryptedPassword?: string; // KMS-encrypted Sidewalk password
}

export interface ReAuthInitiateResponse {
  challengeName: string;
  session: string;
}

export interface ReAuthRespondResponse {
  success: boolean;
  message: string;
}

export interface LocationUpdate {
  objectId: string;
  latitude: number;
  longitude: number;
  timestamp: number;
}

class ObjectsService {
  private apiUrl: string;
  
  constructor() {
    // Get API URL from environment variables
    this.apiUrl = process.env.REACT_APP_API_URL || '';
    if (!this.apiUrl) {
      console.warn('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
  }
  
  // Public method to check if we're in development mode or API URL is not set
  isDevMode(): boolean {
    return !this.apiUrl || process.env.NODE_ENV === 'development';
  }
  
  // Helper method to get the current authenticated user's ID token
  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        throw new Error('No ID token available');
      }
      return session.tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }
  
  // Get all objects
  async getAllObjects(): Promise<TrackedObject[]> {
    try {
      // If API URL is not set, return mock data
      if (!this.apiUrl || process.env.NODE_ENV === 'development') {
        console.warn('Using mock objects data. For real API calls, ensure REACT_APP_API_URL is set.');
        return this.getMockObjects();
      }
      
      // Fix double slash in URL by ensuring proper URL formatting
      const baseUrl = this.apiUrl.endsWith('/')
        ? this.apiUrl.slice(0, -1) // Remove trailing slash if present
        : this.apiUrl;
      
      const token = await this.getAuthToken();
      const response = await axios.get(`${baseUrl}/objects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching objects:', error);
      // Fall back to mock data in case of error
      return this.getMockObjects();
    }
  }
  
  // Get a specific object by ID
  async getObjectById(id: string): Promise<TrackedObject | null> {
    try {
      // If API URL is not set, return mock data
      if (!this.apiUrl || process.env.NODE_ENV === 'development') {
        const mockObjects = this.getMockObjects();
        return mockObjects.find(obj => obj.id === id) || null;
      }
      
      const token = await this.getAuthToken();
      const response = await axios.get(`${this.apiUrl}/objects/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching object with ID ${id}:`, error);
      return null;
    }
  }
  
  // Get the latest location for an object
  async getLatestLocation(id: string): Promise<LocationUpdate | null> {
    try {
      // If API URL is not set, return mock data
      if (!this.apiUrl || process.env.NODE_ENV === 'development') {
        return this.getMockLocationForObject(id);
      }
      
      // Fix double slash in URL by ensuring proper URL formatting
      const baseUrl = this.apiUrl.endsWith('/')
        ? this.apiUrl.slice(0, -1) // Remove trailing slash if present
        : this.apiUrl;
      
      const token = await this.getAuthToken();
      // This endpoint would typically return the most recent location
      const response = await axios.get(`${baseUrl}/objects/${id}/location/latest`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching latest location for object with ID ${id}:`, error);
      // Fall back to mock data in case of error
      return this.getMockLocationForObject(id);
    }
  }
  
  // Create a new object
  async createObject(data: Partial<TrackedObject>): Promise<TrackedObject> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
    
    try {
      const token = await this.getAuthToken();
      const response = await axios.post(`${this.apiUrl}/objects`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating object:', error);
      throw error;
    }
  }
  
  // Update an object
  async updateObject(id: string, data: Partial<TrackedObject>): Promise<TrackedObject> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
    
    try {
      const token = await this.getAuthToken();
      const response = await axios.put(`${this.apiUrl}/objects/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error updating object with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Delete an object
  async deleteObject(id: string): Promise<boolean> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
    
    try {
      const token = await this.getAuthToken();
      await axios.delete(`${this.apiUrl}/objects/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return true;
    } catch (error) {
      console.error(`Error deleting object with ID ${id}:`, error);
      return false;
    }
  }
  
  // Update an object's location
  async updateObjectLocation(id: string, latitude: number, longitude: number): Promise<LocationUpdate> {
    if (this.isDevMode()) {
      // For development without API, simulate a successful update with mock data
      return this.updateMockLocation(id, latitude, longitude);
    }
    
    // Fix double slash in URL by ensuring proper URL formatting
    const baseUrl = this.apiUrl.endsWith('/')
      ? this.apiUrl.slice(0, -1) // Remove trailing slash if present
      : this.apiUrl;
    
    try {
      const token = await this.getAuthToken();
      const response = await axios.post(`${baseUrl}/objects/${id}/location`, {
        latitude,
        longitude
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Update the mock location cache too, to ensure consistency
      this.updateMockLocation(id, latitude, longitude);
      return response.data;
    } catch (error) {
      console.error(`Error updating location for object with ID ${id}:`, error);
      
      // In case of error with the API, still update the mock location
      // so the UI can show the update even if the backend call failed
      return this.updateMockLocation(id, latitude, longitude);
    }
  }
  
  // Initiate re-authentication for an object
  async initiateReAuth(objectId: string, username?: string, password?: string): Promise<ReAuthInitiateResponse> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.apiUrl.endsWith('/')
        ? this.apiUrl.slice(0, -1)
        : this.apiUrl;

      const token = await this.getAuthToken();
      const body: Record<string, string> = {};
      if (username) body.username = username;
      if (password) body.password = password;

      const response = await axios.post(`${baseUrl}/objects/${objectId}/reauth/initiate`, body, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Error initiating re-auth for object ${objectId}:`, error);
      throw error;
    }
  }

  // Respond to re-authentication SMS MFA challenge
  async respondReAuth(objectId: string, code: string, session: string, username: string): Promise<ReAuthRespondResponse> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.apiUrl.endsWith('/')
        ? this.apiUrl.slice(0, -1)
        : this.apiUrl;

      const token = await this.getAuthToken();
      const response = await axios.post(`${baseUrl}/objects/${objectId}/reauth/respond`, {
        code,
        session,
        username
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Error responding to re-auth for object ${objectId}:`, error);
      throw error;
    }
  }

  // Trigger an on-demand location refresh for a specific object
  async refreshObjectLocation(id: string): Promise<any> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }

    try {
      const baseUrl = this.apiUrl.endsWith('/')
        ? this.apiUrl.slice(0, -1)
        : this.apiUrl;

      const token = await this.getAuthToken();
      const response = await axios.post(`${baseUrl}/objects/${id}/refresh`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error
        || error.response?.statusText
        || error.message
        || 'Failed to refresh object location';
      console.error(`Error refreshing location for object ${id}:`, error);
      throw new Error(`Failed to refresh location for object ${id}: ${message}`);
    }
  }

  // Get location history for an object
  async getLocationHistory(id: string): Promise<LocationUpdate[]> {
    if (!this.apiUrl) {
      throw new Error('API URL is not defined. Set REACT_APP_API_URL environment variable.');
    }
    
    try {
      const token = await this.getAuthToken();
      const response = await axios.get(`${this.apiUrl}/objects/${id}/location/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching location history for object with ID ${id}:`, error);
      return [];
    }
  }
  
  // Provide mock data for development and testing
  private getMockObjects(): TrackedObject[] {
    const now = Date.now();
    return [
      {
        id: 'obj-123456',
        name: 'Delivery Truck 1',
        description: 'Main delivery vehicle',
        type: 'truck',
        createdAt: now - 86400000, // 1 day ago
        updatedAt: now
      },
      {
        id: 'obj-234567',
        name: 'Courier 2',
        description: 'Bicycle courier',
        type: 'bicycle',
        createdAt: now - 172800000, // 2 days ago
        updatedAt: now - 3600000 // 1 hour ago
      },
      {
        id: 'obj-345678',
        name: 'Drone 3',
        description: 'Delivery drone',
        type: 'drone',
        createdAt: now - 259200000, // 3 days ago
        updatedAt: now - 1800000 // 30 minutes ago
      }
    ];
  }
  
  // Local cache for mock locations
  private mockLocationsCache: Map<string, LocationUpdate> = new Map();
  
  // Get mock location data for an object (for development without backend)
  getMockLocationForObject(objectId: string): LocationUpdate {
    const now = Date.now();
    
    // Check if we already have a location for this object in our cache
    if (this.mockLocationsCache.has(objectId)) {
      return this.mockLocationsCache.get(objectId)!;
    }
    
    // Generate deterministic but different locations for different objects
    const hash = objectId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const latOffset = (hash % 10) * 0.01;
    const lngOffset = (hash % 7) * 0.01;
    
    const location = {
      objectId,
      latitude: 37.7749 + latOffset,
      longitude: -122.4194 + lngOffset,
      timestamp: now
    };
    
    // Store in cache
    this.mockLocationsCache.set(objectId, location);
    return location;
  }
  
  // Update the mock location for an object in our local cache
  updateMockLocation(objectId: string, latitude: number, longitude: number): LocationUpdate {
    const now = Date.now();
    const location = {
      objectId,
      latitude,
      longitude,
      timestamp: now
    };
    
    // Update cache
    this.mockLocationsCache.set(objectId, location);
    return location;
  }
}

// Create a singleton instance
const objectsService = new ObjectsService();
export default objectsService;
